import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {RedirectRule} from '../../../../../models/redirect-rule/redirect-rule';
import {forkJoin} from 'rxjs';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {AvailableDomainService} from '../../../../../services/cms/available-domain.service';
import {AvailableDomain} from '../../../../../models/cms/available-domain';
import {RedirectRuleService} from '../../../../../services/redirect-rule/redirect-rule.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {IsGranted} from '../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {LandingPageActivityPeriods} from '../../../../../constants/landing-page-activity';

@Component({
  imports: [
    RouterLink,
    ReactiveFormsModule,
    InputSwitchComponent,
    FormsModule
  ],
  selector: 'app-redirect-rule-form',
  styleUrl: './redirect-rule-form.component.scss',
  templateUrl: './redirect-rule-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
@IsGranted(['ROLE_API_REDIRECT_RULE_WRITE', 'REDIRECT_RULE_VIEW'])
@MetaTitle('Redirect link form')
export class RedirectRuleFormComponent implements OnInit {
  loaded = false;
  id: number | null = null;
  redirectRule: RedirectRule | null = null;
  availableDomains: AvailableDomain[] = [];

  form = new FormGroup({
    name: new FormControl<string | null>(null, Validators.required),
    domainId: new FormControl<number | null>(null, Validators.required),
    identifier: new FormControl<string>('', Validators.required),
    redirectUrl: new FormControl<string>('', Validators.required),
    copyQueryParameters: new FormControl<boolean>(false),
    enabled: new FormControl<boolean>(true),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private availableDomainService: AvailableDomainService,
    private redirectRuleService: RedirectRuleService,
  ) {
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.loader.startLoading();
    forkJoin([
      this.availableDomainService.findAll({sort: 'domain'}),
    ]).subscribe(([availableDomains]) => {
      this.availableDomains = availableDomains;

      if (this.id) {
        this.redirectRuleService.findOneById(this.id, {}).subscribe((redirectRule: RedirectRule) => {
          this.redirectRule = redirectRule;

          this.form.patchValue({
            name: this.redirectRule.name,
            domainId: this.redirectRule.domain?.id,
            identifier: this.redirectRule.identifier,
            redirectUrl: this.redirectRule.redirectUrl,
            copyQueryParameters: this.redirectRule.copyQueryParameters,
            enabled: this.redirectRule.enabled,
          });
          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.loader.stopLoading();
        this.loaded = true;
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.isCreate()) {
      this.redirectRuleService.create(params).subscribe((response) => {
        this.loader.stopLoading();
        this.snackbar.success('Redirect rule has been created.');
        this.router.navigateByUrl('/promotion-tools/redirect-rules/' + response.id).then();
      });
    } else if (this.redirectRule?.id) {
      this.redirectRuleService.update(this.redirectRule.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Redirect rule has been modified.');
        this.router.navigateByUrl('/promotion-tools/redirect-rules').then();
      });
    }
  }

  isCreate(): boolean {
    return !this.id;
  }

  protected readonly activityPeriods = LandingPageActivityPeriods;
}
